import {WindowSizeConsumer} from '@albert-io/atomic/helpers/WindowSizeProvider/WindowSizeProvider.react';
import cx from 'classnames';
import type {KeyboardEvent} from 'react';
import React, {useEffect, useRef} from 'react';
import {Popper} from 'react-popper';

import {toModifierArray} from '../../helpers/popper/popperCompat';

import {useMenu} from './MenuContext.react';

// First match wins in document order, so a leading search input is focused for
// search-style menus; otherwise the first interactive item.
const FOCUSABLE_SELECTOR =
  'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="menuitem"]:not([tabindex^="-"]), button:not([disabled]):not([tabindex^="-"]), a[href], [tabindex]:not([tabindex^="-"])';

const modifiers = {
  preventOverflow: {
    padding: 64,
    boundariesElement: 'viewport' as const
  },
  flip: {
    padding: 16
  }
};

interface MenuContentProps extends PropsWithChildrenRequired, PropsWithClassNameOptional {}

const MenuContent = ({children, className = ''}: MenuContentProps) => {
  const {isOpen, close, position, positionFixed, popperProps} = useMenu();
  const menuRef = useRef<HTMLDivElement>(null);
  // The element focused when the menu opened (the trigger), so keyboard
  // dismissal can return focus to it per the ARIA menu-button pattern.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    previouslyFocusedRef.current = (document.activeElement as HTMLElement | null) ?? null;
    menuRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
  }, [isOpen]);

  const closeAndRestoreFocus = () => {
    previouslyFocusedRef.current?.focus();
    close();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!menuRef.current) return;

    const menuItems = Array.from(
      menuRef.current.querySelectorAll('[role="menuitem"]:not([tabindex^="-"])')
    ) as HTMLElement[];

    const currentIndex = menuItems.findIndex((item) => item === document.activeElement);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % menuItems.length;
        menuItems[nextIndex]?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prevIndex = currentIndex <= 0 ? menuItems.length - 1 : currentIndex - 1;
        menuItems[prevIndex]?.focus();
        break;
      }
      // Tab must be intercepted or it would step through items while the menu
      // stays open. Both leave the menu and restore focus to the trigger;
      // this covers Shift+Tab (same key).
      case 'Tab':
      case 'Escape': {
        e.preventDefault();
        closeAndRestoreFocus();
        break;
      }
      default:
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <WindowSizeConsumer>
      {(context) => {
        if (!context) return null;

        // popperProps fields override the Menu defaults (matching react-popper 0.10's
        // trailing-spread semantics).
        const effectiveModifiers = popperProps?.modifiers ?? modifiers;
        const effectivePlacement = popperProps?.placement ?? position;
        const effectiveStrategy = popperProps?.strategy ?? (positionFixed ? 'fixed' : 'absolute');

        return (
          <Popper
            placement={effectivePlacement}
            strategy={effectiveStrategy}
            modifiers={toModifierArray(effectiveModifiers)}
          >
            {({ref, style, placement}) => (
              <div
                ref={ref}
                style={style}
                data-placement={placement}
                className={cx('o-menu-content__wrapper', className, {
                  'o-menu-content__wrapper--show': isOpen
                })}
              >
                <div
                  role='menu'
                  className='o-menu-content rounded-x1 border border-solid border-border-primary background-primary p-x1 shadow-l2'
                  onKeyDown={handleKeyDown}
                  ref={menuRef}
                >
                  {children}
                </div>
              </div>
            )}
          </Popper>
        );
      }}
    </WindowSizeConsumer>
  );
};

export default MenuContent;
